import React, { useState } from "react";
import {
  ListItemText,
  Stack,
  Button,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Box,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CheckIcon from "@mui/icons-material/Check";
import { Link } from "react-router-dom";
import { HabitClass } from "../../generated/model";
import {
  useDeleteHabit,
  getGetUserHabitsQueryKey,
  useCreateHabitEntry,
} from "../../generated/hook";
import { useQueryClient } from "@tanstack/react-query";
import StreakBadge from "../StreakBadge";

interface HabitCalendarHeaderProps {
  habit: HabitClass;
}

const HabitCalendarHeader: React.FC<HabitCalendarHeaderProps> = ({ habit }) => {
  const [isMenuOpen, setIsMenuOpen] = React.useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);

  const queryClient = useQueryClient();

  const deleteHabitMutation = useDeleteHabit({
    mutation: {
      onSuccess: () => {
        // Invalidate and refetch habits to update the UI
        queryClient.invalidateQueries({ queryKey: getGetUserHabitsQueryKey() });
      },
      onError: (error) => {
        console.error("Failed to delete habit:", error);
      },
    },
  });

  const createHabitEntryMutation = useCreateHabitEntry({
    mutation: {
      onSuccess: () => {
        // Invalidate and refetch habits to update the UI
        queryClient.invalidateQueries({ queryKey: getGetUserHabitsQueryKey() });
      },
      onError: (error) => {
        console.error("Failed to create habit entry:", error);
      },
    },
  });

  const handleCreateTodayEntry = () => {
    const today = new Date().toISOString().split("T")[0]; // Format YYYY-MM-DD
    createHabitEntryMutation.mutate({
      data: {
        habitId: habit.id,
        date: today,
        completed: true,
        notes: "",
      },
    });
  };

  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      justifyContent="space-between"
      sx={{ width: "100%", p: 2 }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <ListItemText primary={habit.name} sx={{ m: 0 }} />
          <StreakBadge
            currentStreak={habit.currentStreak}
            longestStreak={habit.longestStreak}
            size="small"
          />
        </Box>
        <ListItemText
          secondary={`Description: ${habit.description || "N/A"} | Frequency: ${
            habit.frequency
          } | Target: ${habit.target}`}
          sx={{ m: 0 }}
        />
      </Box>
      <Stack direction="row" spacing={1}>
        <Button
          onClick={handleCreateTodayEntry}
          color="success"
          size="small"
          disabled={createHabitEntryMutation.isPending}
          variant="outlined"
          sx={{
            mt: 0.5,
            minWidth: "auto",
            px: 1,
            borderRadius: 2,
          }}
          title="Mark as completed today"
        >
          <CheckIcon />
        </Button>
        <Button
          onClick={(event) => {
            setAnchorEl((prev) => (prev ? null : event.currentTarget));
            setIsMenuOpen((prev) => !prev);
          }}
          variant="outlined"
          size="small"
          sx={{
            mt: 0.5,
            minWidth: "auto",
            px: 1,
            borderRadius: 2,
          }}
        >
          <MoreVertIcon />
        </Button>
      </Stack>
      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={() => {
          setIsMenuOpen(false);
          setAnchorEl(null);
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem
          component={Link}
          to={`/habits/edit/${habit.id}`}
          onClick={() => setIsMenuOpen(false)}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            setIsMenuOpen(false);
            setIsDeleteDialogOpen(true);
          }}
        >
          Delete
        </MenuItem>
      </Menu>

      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Delete Habit</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this habit? This action will
            permanently erase all your data for this habit and cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={() => {
              setIsDeleteDialogOpen(false);
              deleteHabitMutation.mutate({ id: habit.id });
            }}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default HabitCalendarHeader;
